/**
 * Theme preference plumbing. The preference lives in the `sf-theme` cookie so
 * the SERVER can stamp `data-theme` into the SSR HTML — no wrong-theme flash.
 * `system` deliberately maps to NO attribute: tokens.css resolves it with a
 * prefers-color-scheme media block, so no inline script is needed either.
 */
export type ThemePreference = "system" | "light" | "dark";

export const THEME_COOKIE = "sf-theme";

export function parseThemeCookie(cookieHeader: string | null): ThemePreference {
  const match = cookieHeader?.match(/(?:^|;\s*)sf-theme=(dark|light|system)(?:;|$)/);
  return (match?.[1] as ThemePreference | undefined) ?? "system";
}

export function dataThemeAttribute(pref: ThemePreference): "light" | "dark" | undefined {
  return pref === "system" ? undefined : pref;
}

/**
 * The page's ground in each scheme — `--sf-color-background` from
 * design/tokens.json, for the one place that needs it before any
 * stylesheet is read: the `theme-color` meta, which is what colours the
 * browser's own toolbar around the page on a phone. Copied rather than
 * imported, because tokens.css is a stylesheet; theme.test.ts fails the
 * moment the two disagree.
 */
export const THEME_GROUND = { light: "#F7F1E7", dark: "#0E0B0A" } as const;

/**
 * The scheme the page is actually drawn in: the stamped choice when there
 * is one, the system's otherwise — for the one thing on the page that is
 * not styled by the stylesheet, Google's button, which is drawn by Google
 * in whichever of its themes it is asked for. Light on the server and
 * anywhere without a media query, where the stylesheet's own default is
 * light too.
 */
export function activeScheme(): "light" | "dark" {
  if (typeof document === "undefined") return "light";
  const stamped = document.documentElement.getAttribute("data-theme");
  if (stamped === "light" || stamped === "dark") return stamped;
  return typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Says when the system's scheme changes under a page that follows it —
 * dusk, on a phone set to switch. The stamped choice is only ever changed
 * from the settings, and the settings never share a page with Google's
 * button. Returns the way to stop listening.
 */
export function watchScheme(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
